import { useEffect } from "react";
import { Row } from "@tanstack/react-table";
import Icon from "@/components/Icon";
import { isCompactLayout } from "@/assets/theme/sizes";

interface ExpandableCountCellProps {
  row: Row<any>;
  count: number;
  total?: number;
  hasItems: boolean;
  showExpandIcon: boolean;
  className?: string;
  autoExpandRowId?: string | null;
  onAutoExpandComplete?: () => void;
}

/**
 * Module-level component for expandable count cells. Defined in its own file
 * so React maintains a stable component identity across renders, which is
 * required for useEffect (auto-expand) to work reliably.
 */
export default function ExpandableCountCell({
  row,
  count,
  total,
  hasItems,
  showExpandIcon,
  className,
  autoExpandRowId,
  onAutoExpandComplete
}: ExpandableCountCellProps) {
  const compact = isCompactLayout();
  const shouldShowIcon = showExpandIcon && hasItems && !compact;
  const displayText = total !== undefined ? `${count} of ${total}` : `${count}`;

  useEffect(() => {
    if (autoExpandRowId && autoExpandRowId === row.id && hasItems && !row.getIsExpanded()) {
      row.getToggleExpandedHandler()();
      onAutoExpandComplete?.();
    }
  }, [autoExpandRowId, row, hasItems, onAutoExpandComplete]);

  return (
    <div
      className={`flex items-center gap-2 ${compact ? "w-auto" : "min-h-12 w-full -mx-4 -my-2 px-4 py-2"} ${hasItems ? "cursor-pointer" : ""} ${className || ""}`}
      onClick={
        hasItems
          ? (e) => {
              e.preventDefault();
              e.stopPropagation();
              row.getToggleExpandedHandler()();
            }
          : undefined
      }
    >
      <span>{displayText}</span>
      {shouldShowIcon && <Icon name={row.getIsExpanded() ? "expand_less" : "expand_more"} />}
    </div>
  );
}
